import React, { useState, useRef, useCallback } from 'react';

/**
 * Real drag-and-drop video upload — Jenn, 2026-09-10: the affiliate intake
 * form was asking non-technical people to record a video, upload it to
 * Google Drive themselves, then find and paste a share link. "Too hard...
 * these are not technical people." This drops straight from their own
 * device onto the page, same pattern as ImageDropZone.
 */
export default function VideoDropZone({ onUpload, hasExisting }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file || !file.type.startsWith('video/')) {
      setError('That\'s not a video file.');
      return;
    }
    setUploading(true);
    setUploaded(false);
    setError(null);
    setFileName(file.name);
    try {
      await onUpload(file);
      setUploaded(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not upload that video.');
    } finally {
      setUploading(false);
    }
  }, [onUpload]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      tabIndex={0}
      className={`border-2 border-dashed rounded-lg px-3 py-5 text-center text-sm cursor-pointer transition-colors
        ${dragging ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-200 hover:border-gray-300'}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {uploading ? (
        <span className="text-gray-400">Uploading {fileName}…</span>
      ) : uploaded ? (
        <span className="text-reset-sage">✓ Uploaded — drop a new one here to replace it</span>
      ) : hasExisting ? (
        <span className="text-gray-500">Already on file — drag a new video here to replace it, or click to browse</span>
      ) : (
        <span className="text-gray-400">Drag your video here, or click to browse — record it on your phone, that's it</span>
      )}
      {error && <p className="text-red-600 mt-1 text-xs">{error}</p>}
    </div>
  );
}
